import { Box, Divider, Stack, Typography } from '@mui/material';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';

import { ApplicationSectionCard } from './applicationSectionChrome';
import { EditableValue, useEditableFields } from './EditableDetailRow';
import { renderValue } from '../../lib/applicationFieldEditing';

const FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'personal.firstName', label: 'First name' },
  { key: 'personal.lastName', label: 'Last name' },
  { key: 'personal.dateOfBirth', label: 'Date of birth' },
  { key: 'personal.address', label: 'Address' },
];

/**
 * The identity profile, editable in place on the Identity tab.
 *
 * These four live in identity-verification's `customers` table and are what the
 * document check matched against — so this tab is where a mismatch is noticed and
 * where it should be correctable. Before this card existed they were only reachable
 * from Overview and the aggregated panel, which meant reading the evidence in one
 * place and acting on it in another.
 *
 * Not the same as the agreement identity, which is contract text the customer typed
 * at the confirm step. The two can legitimately disagree — a passport reading
 * "Priyanka" against an agreement signed "Priya" — and correcting one must not
 * silently rewrite the other.
 *
 * Corrections are overlays: the OCR-derived value is never overwritten, and where a
 * correction exists `EditableValue` renders what identity-verification still holds
 * underneath it.
 *
 * Deliberately rendered outside the tab's lookup gate. An application with no
 * identity session shows every value as "—", and that is exactly when a reviewer who
 * has the customer on the phone needs to record what they were told.
 */
export function ApplicationDeclaredIdentityCard({
  applicationId,
}: {
  applicationId: string;
}) {
  const { data } = useEditableFields(applicationId);
  const byKey = new Map((data?.fields ?? []).map((f) => [f.key, f]));

  // Nothing to show until the descriptor loads — better than a card of dashes.
  if (!data) return null;

  const present = FIELDS.filter(({ key }) => byKey.has(key));
  if (present.length === 0) return null;

  const editableHere = present.filter(({ key }) => byKey.get(key)?.editable).length;

  return (
    <ApplicationSectionCard
      title="Identity profile"
      subtitle={
        editableHere > 0
          ? 'What verification matched against — correct it here if you have better information'
          : 'What verification matched against'
      }
      icon={<BadgeRoundedIcon fontSize="small" />}
    >
      <Stack spacing={1} divider={<Divider flexItem />}>
        {present.map(({ key, label }) => {
          const field = byKey.get(key)!;
          return (
            <Box
              key={key}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 190px) minmax(0, 1fr)' },
                columnGap: 1.5,
                rowGap: 0.25,
                alignItems: 'start',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                {label}
              </Typography>
              <EditableValue fieldKey={key}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    whiteSpace: key === 'personal.address' ? 'pre-wrap' : undefined,
                    ...(field.sourceResolved === false ? { color: 'warning.main' } : null),
                  }}
                >
                  {field.sourceResolved === false
                    ? 'Unknown — identity service unreachable'
                    : renderValue(field.currentValue, field.valueType)}
                </Typography>
              </EditableValue>
            </Box>
          );
        })}
      </Stack>
    </ApplicationSectionCard>
  );
}
